import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';

import { ChargeableHoursTable } from '@lib/chargeable-hours-table';
import { PoCapacityTable } from '@lib/po-capacity-table';
import { TeamCapacityTable } from '@lib/team-capacity-table';
import moment from 'moment';
import { delay, from } from 'rxjs';

import { Agreement, Milestone, Product } from '../schemas';
import { AgreementService, Jira, ProductService } from '../service';

export interface AugmentedItem {
  _source: Agreement | Product;
  JiraTitle: string;
  ClientCode: string;
  BudgetNote: string;
  ServiceType: string | null;
  JobNumber: string;
  Title: string;
  Type: 'Agreement' | 'Product';
  LinkBase: string;
  ProductOwner: string;
  Status: string;
  LastRefreshed: string;
  Hours: string;
  StartDate: string;
  Milestones: Milestone[];
  NotInvoicedYet: number;
}

export interface TimeTableItem {
  time: number;
  timestamp: number;
  lastUpdated: string;
  isPotentiallyHolded: boolean;
}
export type TimeTableData = Record<string, TimeTableItem>;

const ejectStartDate = (entity: Product | Agreement) => {
  if ('AgreementNumber' in entity) return entity.StartDate;

  // For products, find the earliest date from all hour types and milestones
  let date = moment();
  date.subtract(6, 'months');
  const purchased = entity.PurchasedHours || [];
  const unchargeable = entity.UnchargeableHours || [];
  const profitable = entity.ProfitableHours || [];

  // Combine all dates from hours and milestones, find the earliest
  const allHours = [...purchased, ...unchargeable, ...profitable]
    .map((item) => moment(`${item.Year} ${item.Month}`, 'YYYY MMMM'))
    .concat(((entity as Product).Milestones || []).map((m) => moment(m.CreatedAt)));

  for (const itemDate of allHours) {
    if (date.isAfter(itemDate)) {
      date = itemDate;
    }
  }
  return String(`${date.year()}-${('0' + (date.month() + 1)).slice(-2)}-01`);
};

const intoAugmented = (item: Agreement | Product) =>
  ({
    _source: item,
    JiraTitle: item.JiraTitle,
    ClientCode: item.ClientCode,
    BudgetNote: item.BudgetNote,
    ServiceType: 'ServiceType' in item ? item.ServiceType : null,
    JobNumber: 'AgreementNumber' in item ? item.AgreementNumber : item.JobNumber,
    Title: item.Title,
    Type: 'AgreementNumber' in item ? 'Agreement' : 'Product',
    LinkBase: 'AgreementNumber' in item ? '/app/agreements/view' : '/app/products/view',
    ProductOwner: item.ProductOwner ? item.ProductOwner : '',
    Status: item.Status,
    LastRefreshed: 'Loading...',
    Hours: 'Loading...',
    StartDate: ejectStartDate(item),
    Milestones: (item as Product).Milestones || [],
    // Calculate total hours not yet invoiced (status !== 20)
    // Includes both purchased hours and milestone hours (converted from value/hourly rate)
    NotInvoicedYet: [
      ...item.PurchasedHours,
      ...((item as Product).Milestones || []).map((ms) => {
        return {
          Hours: ~~(ms.Value / (ms.HourlyRate || 175)),
          Status: +ms.Status,
        };
      }),
    ]
      .filter(({ Status }) => Status !== 20)
      .reduce((result: number, { Hours }) => result + Hours, 0),
    // Organize hours by type for time tracking
  }) as AugmentedItem;

export interface JiraPreparedItem {
  id: string;
  dates: string[];
  force: boolean;
  payload: object;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatTableModule,
    MatToolbarModule,
    MatTooltipModule,
    MatTooltipModule,
    MatSortModule,
    FormsModule,
    RouterModule,
    ChargeableHoursTable,
    PoCapacityTable,
    TeamCapacityTable,
  ],
  templateUrl: './chargeable-hours-page.html',
})
export class ChargeableHoursPage {
  private agreementService = inject(AgreementService);
  private productService = inject(ProductService);
  private jira = inject(Jira);

  pending = signal(false);
  private statuses = signal(['Active', 'Pending', 'On Hold']);

  private items = computed(() =>
    [...this.productService.products(), ...this.agreementService.agreements()].map(intoAugmented),
  );

  activeItems = computed(() => {
    let items = this.items();
    const statuses = this.statuses();
    items = items.filter((item) => statuses.includes(item.Status));
    return items;
  });

  timeTable = computed<TimeTableData>(() => {
    const result = {};
    for (const [key, data] of Object.entries(this.jira.tables())) {
      const last = data.time[data.time.length - 1][1];
      // Find max hours spent in the last 3 months to detect inactive projects
      const spentRecently = Math.max(...data.time.slice(-3).map((it) => it[1].spent));

      result[key] = {
        time: last.remaining, // Remaining hours from most recent month
        timestamp: moment(data.lastUpdated).utc().local().format('X'),
        lastUpdated: moment(data.lastUpdated).utc().local().calendar(),
        isPotentiallyHolded: spentRecently <= 0, // No activity in recent months = potentially holded
      };
    }

    return result;
  });

  refreshAll(force = false) {
    from(this.activeItems())
      .pipe(delay(100))
      .subscribe((item) => this.jira.getProjectTimelogs(item._source, force));
  }

  refreshOne(item: AugmentedItem) {
    this.jira.getProjectTimelogs(item._source, true);
  }

  constructor() {
    effect(() => {
      this.refreshAll();
    });
  }
}
